import React, { useState, useEffect } from "react";
import { Table, Tag, Input, Button, Card, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { 
  empleadosApi, 
  campusApi, 
  dependenciasApi, 
  tiposContratosApi,
  tiposEmpleadosApi 
} from "../../services/api";
import EmpleadoFormModal from "./EmpleadoFormModal";
import DeleteConfirmModal from "../DeleteConfirmModal";

const TablaEmpleados = () => {
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEmpleado, setEditingEmpleado] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [campus, setCampus] = useState([]);
  const [dependencias, setDependencias] = useState([]);
  const [tiposContratos, setTiposContratos] = useState([]);
  const [tiposEmpleados, setTiposEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [empleadoToDelete, setEmpleadoToDelete] = useState(null);

  // Cargar datos al iniciar el componente
  useEffect(() => {
    Promise.all([
      fetchEmpleados(), 
      fetchCampus(), 
      fetchDependencias(), 
      fetchTiposContratos(),
      fetchTiposEmpleados()
    ])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error cargando datos iniciales:", error);
        toast.error("Error al cargar los datos", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
      });
  }, []);

  // Función para obtener empleados
  const fetchEmpleados = async () => {
    try {
      const response = await empleadosApi.getAll();
      if (response.data.status) {
        const empleadosData = response.data.empleados.map((empleado) => ({
          key: empleado.id.toString(),
          id: empleado.id,
          nombre: empleado.nombre,
          cedula: empleado.cedula,
          cargo: empleado.cargo,
          campus_id: empleado.campus_id,
          campus: empleado.campus.campus,
          dependencia_id: empleado.dependencia_id,
          dependencia: empleado.dependencia.dependencia,
          tipo_contrato_id: empleado.tipo_contrato_id,
          tipo_contrato: empleado.tipo_contrato.tipo_contrato,
          tipo_empleado_id: empleado.tipo_empleado_id,
          tipo_empleado: empleado.tipo_empleado.tipo_empleado,
          estado: empleado.estado
        }));
        setEmpleados(empleadosData);
      }
    } catch (error) {
      console.error("Error obteniendo empleados:", error);
      toast.error("Error al cargar los empleados", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener campus
  const fetchCampus = async () => {
    try {
      const response = await campusApi.getAll();
      if (response.data.status) {
        setCampus(response.data.campus);
      }
    } catch (error) {
      console.error("Error obteniendo campus:", error);
      toast.error("Error al cargar los campus", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener dependencias
  const fetchDependencias = async () => {
    try {
      const response = await dependenciasApi.getAll();
      if (response.data.status) {
        setDependencias(response.data.dependencias);
      }
    } catch (error) {
      console.error("Error obteniendo dependencias:", error);
      toast.error("Error al cargar las dependencias", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener tipos de contratos
  const fetchTiposContratos = async () => {
    try {
      const response = await tiposContratosApi.getAll();
      if (response.data.status) {
        setTiposContratos(response.data.tipos_contratos);
      }
    } catch (error) {
      console.error("Error obteniendo tipos de contratos:", error);
      toast.error("Error al cargar los tipos de contratos", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener tipos de empleados
  const fetchTiposEmpleados = async () => {
    try {
      const response = await tiposEmpleadosApi.getAll();
      if (response.data.status) {
        setTiposEmpleados(response.data.tiposEmpleados);
      }
    } catch (error) {
      console.error("Error obteniendo tipos de empleados:", error);
      toast.error("Error al cargar los tipos de empleados", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Buscar
        </Button>
        <Button
          onClick={() => clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Limpiar
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: <div className="font-medium">Nombre</div>,
      dataIndex: "nombre",
      key: "nombre",
      ...getColumnSearchProps("nombre"),
      render: (text) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2">
            <UserOutlined />
          </div>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div className="font-medium">Cédula</div>,
      dataIndex: "cedula",
      key: "cedula",
      ...getColumnSearchProps("cedula"),
    },
    {
      title: <div className="font-medium">Cargo</div>,
      dataIndex: "cargo",
      key: "cargo",
      ...getColumnSearchProps("cargo"),
    },
    {
      title: <div className="font-medium">Campus</div>,
      dataIndex: "campus",
      key: "campus",
      filters: campus.map((c) => ({ text: c.campus, value: c.campus })),
      onFilter: (value, record) => record.campus === value,
    },
    {
      title: <div className="font-medium">Dependencia</div>,
      dataIndex: "dependencia",
      key: "dependencia",
      filters: dependencias.map((d) => ({ text: d.dependencia, value: d.dependencia })),
      onFilter: (value, record) => record.dependencia === value,
    },
    {
      title: <div className="font-medium">Tipo de Contrato</div>,
      dataIndex: "tipo_contrato",
      key: "tipo_contrato",
      filters: tiposContratos.map((tc) => ({ text: tc.tipo_contrato, value: tc.tipo_contrato })),
      onFilter: (value, record) => record.tipo_contrato === value,
    },
    {
      title: <div className="font-medium">Tipo de Empleado</div>,
      dataIndex: "tipo_empleado",
      key: "tipo_empleado",
      filters: tiposEmpleados.map((te) => ({ text: te.tipo_empleado, value: te.tipo_empleado })),
      onFilter: (value, record) => record.tipo_empleado === value,
    },
    {
      title: <div className="font-medium">Estado</div>,
      dataIndex: "estado",
      key: "estado",
      filters: [
        { text: "Activo", value: "activo" },
        { text: "Inactivo", value: "inactivo" },
      ],
      onFilter: (value, record) => record.estado === value,
      render: (estado) => (
        <Tag
          color={estado === "activo" ? "green" : "red"}
          className="py-1 px-2"
        >
          {estado === "activo" ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: <div className="font-medium text-center">Acciones</div>,
      key: "acciones",
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Tooltip title="Editar empleado">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              type="primary"
              size="middle"
              className="bg-blue-500 hover:bg-blue-700"
            />
          </Tooltip>
          <Tooltip title="Eliminar empleado">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              type="primary"
              danger
              size="middle"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    fetchEmpleados();
  };

  const showModal = () => {
    setIsModalVisible(true);
    setEditingEmpleado(null);
  };

  const handleEdit = (empleado) => {
    setEditingEmpleado(empleado);
    setIsModalVisible(true);
  };

  const handleDelete = (empleado) => {
    setEmpleadoToDelete(empleado);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await empleadosApi.delete(empleadoToDelete.id);
      toast.success(`Empleado ${empleadoToDelete.nombre} eliminado correctamente`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setDeleteModalVisible(false);
      setEmpleadoToDelete(null);
      fetchEmpleados(); // Recargar la lista de empleados
    } catch (error) {
      console.error("Error eliminando empleado:", error);
      let errorMsg = "Error al eliminar el empleado";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    setEditingEmpleado(null);
    fetchEmpleados(); // Recargar la lista después de un cambio exitoso
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
        <Spin size="large" tip="Cargando empleados..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Administración de Empleados
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Nuevo Empleado
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <Input
              placeholder="Buscar empleado..."
              className="w-72"
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            type="default"
            className="h-10"
          >
            Reiniciar filtros
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={empleados.filter(
            (item) =>
              item.nombre.toLowerCase().includes(searchText.toLowerCase()) ||
              (item.cedula &&
                item.cedula.toLowerCase().includes(searchText.toLowerCase())) ||
              item.cargo.toLowerCase().includes(searchText.toLowerCase())
          )}
          rowClassName={() =>
            "hover:bg-blue-50 transition-colors cursor-pointer"
          }
          scroll={{ y: "calc(100vh - 350px)" }}
          pagination={{
            total: empleados.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} empleados`,
          }}
          className="border border-gray-200 rounded-md"
        />

        {/* Modal para crear/editar empleados */}
        <EmpleadoFormModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={handleModalSuccess}
          editingEmpleado={editingEmpleado}
          campus={campus}
          dependencias={dependencias}
          tiposContratos={tiposContratos}
          tiposEmpleados={tiposEmpleados}
        />

        {/* Modal de confirmación para eliminar empleado */}
        <DeleteConfirmModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
          title="Eliminar Empleado"
          itemDescription="al empleado"
          itemName={empleadoToDelete ? empleadoToDelete.nombre : ""}
        />
      </Card>
    </div>
  );
};

export default TablaEmpleados;